import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";

const InitializingPage = () => {
    const navigate = useNavigate();
    const { user_id, project_id } = useParams();

    const [dataStatus, setDataStatus] = useState({
        namespace_pod: false,
        dependencies: false,
    });
    const [launching, setLaunching] = useState(false);
    const [forcedLaunch, setForcedLaunch] = useState(false);

    const dependenciesTimerRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);


  useEffect(() => {
    if (!user_id || !project_id) return;

    const socket = io("https://websocket-streaming.erioon.com", {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });

    socket.on("connect", () => {
      socket.emit("stream_project_initialization", { user_id, project_id });
    });

    socket.on("disconnect", () => {
      console.warn("Disconnected from stream — attempting reconnect...");
    });

    socket.on("error", (err) => {
      console.error("❌ Stream error:", err);
    });

    socket.on("update", (data) => {
      if (!data) return;
      
      setDataStatus((prev) => ({
        namespace_pod: data.namespace_pod ?? prev.namespace_pod,
        dependencies: data.dependencies ?? prev.dependencies,
      }));

      if (data.namespace_pod && !data.dependencies && !dependenciesTimerRef.current) {
        dependenciesTimerRef.current = setTimeout(() => {
          setForcedLaunch(true);
        }, 6000);
      }

      if (data.dependencies && dependenciesTimerRef.current) {
        clearTimeout(dependenciesTimerRef.current);
        dependenciesTimerRef.current = null;
      }

      if ((data.dependencies || forcedLaunch) && !launching) {
        setTimeout(() => {
           setLaunching(true); 
        }, 2000);
        setTimeout(() => navigate(`/project_panel/${project_id}/databases`), 4000);
      }
    });

    return () => {
      socket.disconnect();
      if (dependenciesTimerRef.current) clearTimeout(dependenciesTimerRef.current);
    };
  }, [user_id, project_id, forcedLaunch, launching, navigate]);


    const steps = [
        { completed: dataStatus.namespace_pod },
        { completed: dataStatus.dependencies || forcedLaunch },
        { completed: launching },
    ];

    const renderMessages = () => {
        const msgs = [];

        msgs.push(
            <p className="fade-in" key="namespace">
                <i className={dataStatus.namespace_pod ? "fas fa-check" : "fas fa-spinner fa-spin"}></i>{" "}
                {dataStatus.namespace_pod
                    ? "Compute environment created successfully."
                    : "Creating compute environment..."}
            </p>
        );

        if (dataStatus.namespace_pod) {
            msgs.push(
                <p className="fade-in" key="dependencies">
                    <i
                        className={
                            dataStatus.dependencies
                                ? "fas fa-check"
                                : forcedLaunch
                                    ? "fas fa-check"
                                    : "fas fa-spinner fa-spin"
                        }
                    ></i>{" "}
                    {dataStatus.dependencies
                        ? "All required packages installed successfully."
                        : forcedLaunch
                            ? "Dependencies are still being installed in the background..."
                            : "Installing necessary packages in the compute environment..."}
                </p>
            );
        }

        if (dataStatus.dependencies || forcedLaunch) {
            msgs.push(
                <p className="fade-in" key="launching">
                    <i className="fas fa-spinner fa-spin"></i>{" "}
                    {launching ? "Opening project..." : "Launching experience..."}
                </p>
            );
        }

        return msgs;
    };

    return (
        <div className="initializing_project_container">
            <p className="initializing_project_title fade-in">Initializing your project experience...</p>
            <p className="initializing_project_sub fade-in">
                This process takes less than a minute. Sit tight while we set everything up for you!
            </p>

            <div className="loading_bar_project fade-in">
                {steps.map((step, idx) => (
                    <div
                        key={idx}
                        className={`loading_bar_segment ${step.completed ? "completed" : ""}`}
                    ></div>
                ))}
            </div>

            <div className="loading_doughnut">{renderMessages()}</div>

            <div className="alert_mex_cont">
                <p className="alert_mex fade-in">
                    You're seeing this because you initialized a project in Erioon
                </p>
                <p className="alert_mex_sub fade-in">
                    Read our <strong>documentation</strong> for more details
                </p>
            </div>
        </div>
    );
};

export default InitializingPage;
